import React, { useState, useEffect } from "react";
import { IconCoin, IconUser, IconUsers, IconClock, IconPercentage } from "@tabler/icons-react";
import { useStaffOptions } from "../controllers/tips.controller";

export default function TipInputCard({
  billTotal = 0,
  tipAmount = 0,
  onTipChange,
  tipData = {},
  onTipDataChange,
  storeSettings = {},
  currency = "Rs.",
  defaultStaff = null,
}) {
  const { staff } = useStaffOptions();

  // Read configured quick percentages (default "5,10,15")
  const defaultPercentStr = storeSettings?.tip_default_percentages || storeSettings?.tipDefaultPercentages || "5,10,15";
  const percentageChips = defaultPercentStr
    .split(",")
    .map((p) => parseFloat(p.trim()))
    .filter((p) => !isNaN(p) && p > 0);

  const [activePercent, setActivePercent] = useState(null);

  const distributionType = tipData?.distribution_type || storeSettings?.tip_default_type || "direct";
  const employeeId = tipData?.employee_id || "";
  const poolDept = tipData?.pool_department || "Restaurant Service";

  // Preselect default staff if none chosen and distribution is direct
  useEffect(() => {
    if (distributionType === "direct" && !employeeId && defaultStaff) {
      onTipDataChange({
        ...tipData,
        distribution_type: "direct",
        employee_id: defaultStaff.id || defaultStaff.employee_id || defaultStaff.user_id || "",
        employee_name: defaultStaff.name || defaultStaff.username || "",
      });
    }
  }, [defaultStaff, distributionType]);

  const handleChipClick = (pct) => {
    if (activePercent === pct) {
      // Toggle off
      setActivePercent(null);
      onTipChange(0);
    } else {
      setActivePercent(pct);
      const calculatedTip = Math.round(((billTotal * pct) / 100) * 100) / 100;
      onTipChange(calculatedTip);
    }
  };

  const handleCustomTipChange = (e) => {
    setActivePercent(null);
    const val = parseFloat(String(e.target.value)?.replace(/,/g, ""));
    const sanitized = isNaN(val) ? 0 : Math.max(0, val);
    onTipChange(sanitized);
  };

  const handleDistributionTypeChange = (type) => {
    const updated = {
      ...tipData,
      distribution_type: type,
    };
    if (type === "pool" && !updated.pool_department) {
      updated.pool_department = "Restaurant Service";
    }
    onTipDataChange(updated);
  };

  const handleStaffChange = (e) => {
    const selectedId = e.target.value;
    const found = staff.find((s) => String(s.raw_id || s.id) === String(selectedId) || String(s.id) === String(selectedId));
    onTipDataChange({
      ...tipData,
      employee_id: selectedId,
      employee_name: found ? found.name : "",
    });
  };

  const handlePoolDeptChange = (e) => {
    onTipDataChange({
      ...tipData,
      pool_department: e.target.value,
    });
  };

  const currentTip = parseFloat(tipAmount) || 0;
  const totalPayableWithTip = Number((billTotal + currentTip).toFixed(2));

  return (
    <div className="bg-gradient-to-br from-amber-50/70 to-orange-50/40 dark:from-amber-950/20 dark:to-zinc-900 border border-amber-200/80 dark:border-amber-900/40 rounded-2xl p-4 shadow-xs space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <IconCoin size={17} />
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
            Tip / Gratuity (Optional)
          </span>
        </div>
        {currentTip > 0 && (
          <span className="text-xs font-extrabold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-full">
            +{currency}{currentTip.toFixed(2)}
          </span>
        )}
      </div>

      {/* Percentage Presets & Amount Input */}
      <div>
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {percentageChips.map((pct) => (
            <button
              key={pct}
              type="button"
              onClick={() => handleChipClick(pct)}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                activePercent === pct
                  ? "bg-amber-500 text-white shadow-xs"
                  : "bg-white dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-gray-200 dark:border-zinc-700"
              }`}
            >
              <span>{pct}%</span>
              <span className="text-[10px] opacity-75 font-normal">
                ({currency}{Math.round((billTotal * pct) / 100)})
              </span>
            </button>
          ))}
          {currentTip > 0 && (
            <button
              type="button"
              onClick={() => {
                setActivePercent(null);
                onTipChange(0);
              }}
              className="text-[11px] font-semibold text-gray-400 hover:text-red-500 px-2 py-1 transition"
            >
              Clear Tip
            </button>
          )}
        </div>

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
            {currency}
          </span>
          <input
            type="number"
            min="0"
            step="any"
            value={tipAmount === 0 ? "" : tipAmount}
            onChange={handleCustomTipChange}
            placeholder="0.00 (Enter tip amount)"
            className="w-full pl-10 pr-3 py-2 bg-white dark:bg-zinc-900 border border-amber-200 dark:border-zinc-800 rounded-xl text-sm font-semibold text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500 shadow-xs"
          />
        </div>
      </div>

      {/* Distribution Allocation (Only visible when tip > 0) */}
      {currentTip > 0 && (
        <div className="pt-2 border-t border-amber-200/50 dark:border-zinc-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-600 dark:text-zinc-400">
              Allocation / Recipient:
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleDistributionTypeChange("direct")}
                className={`text-[11px] px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 ${
                  distributionType === "direct"
                    ? "bg-amber-500 text-white"
                    : "bg-white dark:bg-zinc-800 text-gray-600 dark:text-zinc-300"
                }`}
              >
                <IconUser size={12} /> Direct Staff
              </button>
              <button
                type="button"
                onClick={() => handleDistributionTypeChange("pool")}
                className={`text-[11px] px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 ${
                  distributionType === "pool"
                    ? "bg-amber-500 text-white"
                    : "bg-white dark:bg-zinc-800 text-gray-600 dark:text-zinc-300"
                }`}
              >
                <IconUsers size={12} /> Shift Pool
              </button>
              <button
                type="button"
                onClick={() => handleDistributionTypeChange("pending")}
                className={`text-[11px] px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 ${
                  distributionType === "pending"
                    ? "bg-amber-500 text-white"
                    : "bg-white dark:bg-zinc-800 text-gray-600 dark:text-zinc-300"
                }`}
              >
                <IconClock size={12} /> Pending
              </button>
            </div>
          </div>

          {/* Conditional Input based on distribution type */}
          {distributionType === "direct" && (
            <select
              value={employeeId}
              onChange={handleStaffChange}
              className="select select-bordered select-xs w-full rounded-lg bg-white dark:bg-zinc-900 text-xs text-gray-800 dark:text-zinc-200"
            >
              <option value="">Select Serving Staff Member...</option>
              {(staff || []).map((s) => (
                <option key={s.id} value={s.raw_id || s.id}>
                  {s.name} ({s.role})
                </option>
              ))}
            </select>
          )}

          {distributionType === "pool" && (
            <select
              value={poolDept}
              onChange={handlePoolDeptChange}
              className="select select-bordered select-xs w-full rounded-lg bg-white dark:bg-zinc-900 text-xs text-gray-800 dark:text-zinc-200"
            >
              <option value="Restaurant Service">Restaurant Service Pool</option>
              <option value="Kitchen Pool">Kitchen / Back of House Pool</option>
              <option value="Housekeeping Pool">Housekeeping Pool</option>
              <option value="Front Office Pool">Front Office Pool</option>
              <option value="General Staff Pool">General Staff Pool (All)</option>
            </select>
          )}

          {distributionType === "pending" && (
            <p className="text-[10px] text-gray-500 dark:text-zinc-400 italic">
              Tip will be recorded as unallocated. Manager can distribute it later in the Tip Management module.
            </p>
          )}
        </div>
      )}

      {/* Recalculated Total Pay Preview */}
      <div className="pt-2 border-t border-amber-200/50 dark:border-zinc-800 flex items-center justify-between text-xs">
        <span className="text-gray-500 dark:text-zinc-400 font-medium">
          Total Payable (Bill + Tip):
        </span>
        <span className="font-extrabold text-sm text-gray-900 dark:text-white">
          {currency}{totalPayableWithTip.toFixed(2)}
        </span>
      </div>
    </div>
  );
}
